/**
 * ReleasePanel + FinalReportsList — Gutachter-spezifische UI für den
 * Round-Trip-Workflow (PDF prüfen, freigeben, Magic-Link an Endkunde).
 */
import React, { useState } from "react";
import { FileText, Download, Loader2, Eye, CheckCircle2, AlertCircle, Upload, Link2, Copy, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";
import { toast } from "sonner";


export function ReleasePanel({ caseObj, accessToken, onRelease, onRequestRevision, onSendToCustomer, saving }) {
  const [revisionMode, setRevisionMode] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [sendMode, setSendMode] = useState(false);
  const [customerEmail, setCustomerEmail] = useState(caseObj.customer?.email || "");
  const [magicLink, setMagicLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const reports = caseObj.final_reports || [];
  const latest = reports[reports.length - 1];
  if (!latest) return null;

  const pdfUrl = `${API}/cases/${caseObj.id}/final-pdf/${latest.id}?auth=${accessToken || ""}`;

  const persistedToken = caseObj.customer_magic_link?.token;
  const activeToken = magicLink?.magic_link_token || persistedToken;
  const linkExpiresAt = magicLink?.magic_link_expires_at || caseObj.customer_magic_link?.expires_at;
  const linkSignedAt = caseObj.customer_magic_link?.receipt_signed_at;
  const linkSignerName = caseObj.customer_magic_link?.receipt_signer_name;
  const linkViewCount = caseObj.customer_magic_link?.view_count || 0;
  const linkLastViewed = caseObj.customer_magic_link?.last_viewed_at;
  const publicUrl = activeToken ? `${window.location.origin}/g/${activeToken}` : null;

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link in die Zwischenablage kopiert");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
    }
  };

  const handleSend = async () => {
    const result = await onSendToCustomer(customerEmail);
    if (result?.magic_link_token) setMagicLink(result);
  };

  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50/40 p-5 space-y-4" data-testid="release-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl brand-gradient-bg flex items-center justify-center">
          <FileText className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-zinc-900 text-lg">
            {caseObj.status === "ready_for_gutachter" ? "Gutachten zur Freigabe bereit"
              : caseObj.status === "sent_to_customer" ? "An Kunden gesendet"
              : caseObj.status === "released" ? "Gutachten freigegeben"
              : "Finales Gutachten"}
          </div>
          <div className="text-sm text-zinc-600">
            Backoffice hat das finale Gutachten erstellt — Version {latest.version}.
            {caseObj.status === "ready_for_gutachter" ? " Bitte prüfen und entscheiden." : ""}
          </div>
        </div>
        <a href={pdfUrl} download={latest.filename}
          className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
          data-testid="release-download-link">
          <Download className="h-4 w-4" /> Download
        </a>
      </div>

      <div className="rounded-xl border border-emerald-200 bg-white overflow-hidden" data-testid="release-pdf-viewer">
        <iframe src={pdfUrl} title={latest.filename} className="w-full h-[70vh] min-h-[480px] bg-zinc-100" />
      </div>

      {persistedToken && !sendMode && !revisionMode && (
        <div className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-4 space-y-2" data-testid="magic-link-status">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-semibold text-cyan-900 inline-flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Endkunden-Link aktiv
              {linkSignedAt ? (
                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white">Empfang bestätigt</span>
              ) : linkViewCount > 0 ? (
                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">{linkViewCount}× geöffnet</span>
              ) : (
                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-zinc-200 text-zinc-700">Wartet auf Kunde</span>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={copyLink} className="text-xs border-cyan-300 text-cyan-800" data-testid="magic-link-copy-btn">
              {copied ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
              {copied ? "Kopiert" : "Link kopieren"}
            </Button>
          </div>
          {publicUrl && (
            <div className="font-mono text-[11px] text-cyan-900 break-all bg-white border border-cyan-100 rounded px-3 py-2">{publicUrl}</div>
          )}
          <div className="text-[11px] text-zinc-600 grid sm:grid-cols-3 gap-1.5">
            {linkSignedAt ? (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                Bestätigt von <strong className="font-semibold">{linkSignerName || "Kunde"}</strong> am {new Date(linkSignedAt).toLocaleString("de-DE")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Noch nicht bestätigt</span>
            )}
            {linkLastViewed && (
              <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Zuletzt geöffnet: {new Date(linkLastViewed).toLocaleString("de-DE")}</span>
            )}
            {linkExpiresAt && (
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Gültig bis {new Date(linkExpiresAt).toLocaleDateString("de-DE")}</span>
            )}
          </div>
        </div>
      )}

      {!revisionMode && !sendMode && (
        <div className="flex flex-wrap gap-2.5" data-testid="release-actions">
          {caseObj.status === "ready_for_gutachter" && (
            <>
              <Button onClick={onRelease} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none min-w-[180px]"
                data-testid="release-approve-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Freigeben
              </Button>
              <Button variant="outline" onClick={() => setRevisionMode(true)}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 flex-1 sm:flex-none min-w-[180px]"
                data-testid="release-revision-btn">
                <AlertCircle className="h-4 w-4 mr-2" /> Korrektur anfordern
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => setSendMode(true)}
            className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 flex-1 sm:flex-none min-w-[180px]"
            data-testid="release-send-btn">
            <Upload className="h-4 w-4 mr-2" /> {persistedToken ? "Neuen Link erzeugen" : "An Kunden senden"}
          </Button>
        </div>
      )}

      {revisionMode && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4 space-y-3" data-testid="revision-form">
          <div className="text-sm font-semibold text-rose-900">Was soll korrigiert werden?</div>
          <Textarea value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} rows={4}
            placeholder="z. B. Im Schadendetailbereich fehlt die Tiefe der Beschädigung an der Stoßstange…"
            className="bg-white" data-testid="revision-note-input" />
          <div className="flex gap-2">
            <Button
              onClick={async () => { await onRequestRevision(revisionNote); setRevisionMode(false); setRevisionNote(""); }}
              disabled={saving || !revisionNote.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="revision-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Korrektur anfragen
            </Button>
            <Button variant="outline" onClick={() => { setRevisionMode(false); setRevisionNote(""); }}>Abbrechen</Button>
          </div>
        </div>
      )}

      {sendMode && (
        <div className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-4 space-y-3" data-testid="send-form">
          <div className="text-sm font-semibold text-cyan-900">Magic-Link für den Endkunden erzeugen</div>
          <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="kunde@beispiel.de (optional, nur als Notiz)" className="bg-white" data-testid="send-email-input" />
          <div className="text-xs text-zinc-500">
            Es wird ein persönlicher, login-freier Link erzeugt. Der Kunde kann das PDF einsehen, herunterladen
            und den Empfang per Touch-Unterschrift bestätigen. Link gültig 30 Tage.
          </div>
          {magicLink?.magic_link_token ? (
            <div className="rounded-lg border border-emerald-200 bg-white p-3 space-y-2" data-testid="magic-link-result">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">Link bereit</div>
              <div className="font-mono text-[12px] text-zinc-900 break-all">{window.location.origin}/g/{magicLink.magic_link_token}</div>
              <Button size="sm" onClick={copyLink} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="magic-link-copy-result">
                {copied ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                {copied ? "Kopiert!" : "In Zwischenablage kopieren"}
              </Button>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button onClick={handleSend} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="send-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {magicLink?.magic_link_token ? "Neuen Link erzeugen" : "Magic-Link erzeugen"}
            </Button>
            <Button variant="outline" onClick={() => { setSendMode(false); setMagicLink(null); }}>Schließen</Button>
          </div>
        </div>
      )}
    </div>
  );
}


export function FinalReportsList({ caseObj, accessToken }) {
  const reports = (caseObj.final_reports || []).slice().reverse();
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="final-reports-list">
      <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50/50">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500">
          Finale Gutachten · {reports.length} {reports.length === 1 ? "Version" : "Versionen"}
        </div>
      </div>
      <div className="divide-y divide-zinc-100">
        {reports.map((r) => {
          const url = `${API}/cases/${caseObj.id}/final-pdf/${r.id}?auth=${accessToken || ""}`;
          return (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition" data-testid={`final-report-${r.version}`}>
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                <FileText className="h-4 w-4 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">Gutachten — Version {r.version}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Erstellt von {r.generated_by_name || "—"} ·{" "}
                  {r.generated_at ? new Date(r.generated_at).toLocaleString("de-DE") : ""}
                </div>
              </div>
              <a href={url} target="_blank" rel="noreferrer"
                className="mr-3 text-cyan-700 hover:text-cyan-800 text-xs font-semibold inline-flex items-center gap-1"
                data-testid={`final-report-view-${r.version}`}>
                <Eye className="h-3.5 w-3.5" /> Ansehen
              </a>
              <a href={url} download={r.filename}
                className="text-zinc-700 hover:text-zinc-900 text-xs font-semibold inline-flex items-center gap-1"
                data-testid={`final-report-download-${r.version}`}>
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}
